import { Component, Input, ChangeDetectorRef, inject } from '@angular/core';
import { LogError, Metadata, RunView } from '@memberjunction/core';
import { GraphQLDataProvider } from '@memberjunction/graphql-dataprovider';
import {
    marketingExperimentEntity,
    marketingExperimentStatusEntity,
    marketingExperimentTierEntity,
    marketingSprintEntity,
} from 'mj_generatedentities';
import { runAction } from '../../shared/run-action.util';

type ExperimentRow = Pick<
    marketingExperimentEntity,
    'ID' | 'Code' | 'Name' | 'Hypothesis' | 'Tactic' | 'ExperimentStatus' | 'ExperimentTier' | 'ExperimentDecision'
    | 'Channel' | 'Budget' | 'DecisionDate' | 'StartDate' | 'ProposalConfidence' | 'RequiresOverride'
    | 'KeepThreshold' | 'IterateRangeMin' | 'IterateRangeMax' | 'KillThreshold'
    | 'StrategicConnectionNote' | 'PriorLearningNotes' | 'CompanyID'
>;

interface SprintCapacityTier {
    tier: string;
    free: number;
    total: number;
}

interface SprintCapacityResp {
    success?: boolean;
    sprintLabel?: string;
    tiers?: SprintCapacityTier[];
    wipCap?: number;
    wipUsed?: number;
}

@Component({
    standalone: false,
    selector: 'mkt-ws-experiments-overview',
    template: `
<mkt-ws-page-head
    [eyebrow]="headerEyebrow"
    [title]="'What the agent wants to test — and what it\\'s testing now.'"
    [sub]="'The agent proposes experiments; you approve, edit, or reject. Every decision traces back to an experiment. KEEP · ITERATE · KILL on every decision date. Winners graduate to Programs.'"
    [hasActions]="true">
    <div actions>
        <button class="ex__btn ex__btn--primary" type="button" [disabled]="agentBusy" (click)="onRunExperimentationAgent()" title="Invoke the Marketing Experimentation Agent against the current company. The agent reads strategy + recent learnings + channel performance, proposes a Tier-validated experiment, and writes it as a Proposed row.">
            <i class="fa-solid" [class.fa-flask]="!agentBusy" [class.fa-spinner]="agentBusy" [class.fa-spin]="agentBusy"></i>
            {{ agentBusy ? 'Agent running…' : 'Run experimentation agent' }}
        </button>
        <button *ngIf="isSandbox" class="ex__btn" type="button" [disabled]="advanceBusy" (click)="onSimulateWeek()" title="Sandbox only — advance time by 1 week AND auto-decide any Active experiment whose decision date has passed. Synthetic metric is drawn from each experiment's Keep/Iterate/Kill bands so you see a mix of outcomes. Triggers all downstream side-effects (Agent Learnings, StrategySection confidence bumps).">
            <i class="fa-solid" [class.fa-forward]="!advanceBusy" [class.fa-spinner]="advanceBusy" [class.fa-spin]="advanceBusy"></i>
            {{ advanceBusy ? 'Simulating…' : 'Simulate next week' }}
        </button>
        <button *ngIf="isSandbox" class="ex__btn" type="button" [disabled]="resetBusy" (click)="onResetSandbox('KeepStrategy')" title="Sandbox only — wipe blogs, social, newsletters, plans, sources, experiments and learnings, but PRESERVE the Strategy Document + Foundations. Use to re-test the experimentation loop without redoing onboarding.">
            <i class="fa-solid" [class.fa-broom]="!resetBusy" [class.fa-spinner]="resetBusy" [class.fa-spin]="resetBusy"></i>
            {{ resetBusy ? 'Resetting…' : 'Reset content (keep strategy)' }}
        </button>
        <button *ngIf="isSandbox" class="ex__btn ex__btn--danger" type="button" [disabled]="resetBusy" (click)="onResetSandbox('Full')" title="Sandbox only — full wipe including Strategy Document and Foundations. Tenant returns to a completely clean state, onboarding must be re-run.">
            <i class="fa-solid" [class.fa-rotate-left]="!resetBusy" [class.fa-spinner]="resetBusy" [class.fa-spin]="resetBusy"></i>
            {{ resetBusy ? 'Resetting…' : 'Reset sandbox (full)' }}
        </button>
        <button class="ex__btn" type="button" (click)="goto('experiments/sprint-review')"><i class="fa-solid fa-clock-rotate-left"></i> Sprint review</button>
        <button class="ex__btn" type="button" (click)="goto('experiments/propose')"><i class="fa-solid fa-plus"></i> Propose yours</button>
    </div>
</mkt-ws-page-head>

<div *ngIf="agentStatusMessage" class="ex__agent-status" [class.ex__agent-status--err]="agentStatusError">
    <i class="fa-solid" [class.fa-circle-info]="!agentStatusError" [class.fa-triangle-exclamation]="agentStatusError"></i>
    <span>{{ agentStatusMessage }}</span>
    <button class="ex__agent-status-x" type="button" (click)="agentStatusMessage = ''" title="Dismiss"><i class="fa-solid fa-xmark"></i></button>
</div>

<div *ngIf="loading" class="ex__loading"><i class="fa-solid fa-spinner fa-spin"></i> Loading experiments…</div>

<ng-container *ngIf="!loading">

    <!-- Proposed by the agent -->
    <mkt-ws-card class="ex__sec" [hasHead]="true" [noPadding]="true">
        <div head>
            <div style="display:flex; align-items:center; gap:8px;">
                <span class="ex__caps-ink">Proposed by the agent</span>
                <mkt-ws-pill variant="info">{{ proposed.length }} drafts</mkt-ws-pill>
            </div>
            <span class="ex__sub">Approve / edit / reject · rationale on each</span>
        </div>
        <ng-container *ngIf="proposed.length > 0; else noProposals">
            <div *ngFor="let row of proposed" class="ex__row">
                <div class="ex__row-head">
                    <div style="display:flex; align-items:center; gap:6px; flex-wrap: wrap;">
                        <mkt-ws-pill variant="muted">{{ row.ExperimentTier || '—' }}</mkt-ws-pill>
                        <mkt-ws-pill variant="muted" *ngIf="row.Channel">{{ row.Channel }}</mkt-ws-pill>
                        <mkt-ws-pill [variant]="confidenceVariant(row.ProposalConfidence)">Confidence {{ row.ProposalConfidence || 'unknown' }}</mkt-ws-pill>
                        <mkt-ws-pill variant="warn" *ngIf="row.RequiresOverride" [title]="overrideTitle(row)">Held for Review · over tier cap</mkt-ws-pill>
                    </div>
                    <span class="ex__ts">{{ row.Code || row.ID.slice(0, 8) }}</span>
                </div>
                <div class="ex__row-title">{{ row.Name || row.Hypothesis || 'Untitled experiment' }}</div>
                <div class="ex__row-body" *ngIf="row.Hypothesis && row.Name">{{ row.Hypothesis }}</div>
                <div class="ex__why" *ngIf="row.StrategicConnectionNote || row.PriorLearningNotes">
                    <i class="fa-solid fa-sparkles"></i>
                    <div>
                        <span class="ex__caps">Why this</span>
                        <span class="ex__why-text">{{ row.StrategicConnectionNote || row.PriorLearningNotes }}</span>
                    </div>
                </div>
                <div class="ex__actions">
                    <button class="ex__btn ex__btn--primary" type="button" [disabled]="busyId === row.ID" (click)="onApprove(row)">
                        <i class="fa-solid fa-check"></i> Approve &amp; schedule
                    </button>
                    <button class="ex__btn" type="button" (click)="openDetail(row.ID)"><i class="fa-solid fa-pen"></i> Edit</button>
                    <button class="ex__btn" type="button" (click)="openDetail(row.ID)"><i class="fa-solid fa-arrow-up-right-from-square"></i> Detail</button>
                </div>
                <div class="ex__row-meta" *ngIf="busyMessage[row.ID]">{{ busyMessage[row.ID] }}</div>
            </div>
        </ng-container>
        <ng-template #noProposals>
            <div class="ex__empty">No experiments are awaiting approval. The agent's next proposal cycle will surface them here.</div>
        </ng-template>
    </mkt-ws-card>

    <!-- Sprint clock + confidence framework -->
    <div class="ex__split">
        <mkt-ws-card class="ex__split-main">
            <div class="ex__sprint-head">
                <div>
                    <div class="ex__caps">{{ currentSprintLabel }} · 2-week cycle</div>
                    <div class="ex__sprint-title">{{ currentSprintRange || 'No active sprint' }}</div>
                </div>
                <mkt-ws-pill variant="warn" *ngIf="sprintDaysRemaining != null"><i class="fa-solid fa-clock"></i> {{ sprintDaysRemaining }} days remaining</mkt-ws-pill>
            </div>
            <div class="ex__sprint-bar-labels">
                <span style="flex:1;">Plan</span>
                <span style="flex:3;">Run</span>
                <span style="flex:1;">Review</span>
                <span style="flex:1;">Decide</span>
            </div>
            <div class="ex__sprint-bar">
                <div *ngFor="let phase of sprintPhases" class="ex__sprint-seg" [class.ex__sprint-seg--active]="phase.active" [class.ex__sprint-seg--current]="phase.current" [style.flex]="phase.flex"></div>
            </div>
            <div class="ex__sprint-stats">
                <div><div class="ex__caps">Active</div><div class="ex__big">{{ stats.active }}</div></div>
                <div><div class="ex__caps">Decisions due</div><div class="ex__big">{{ stats.decisionsDue }}</div></div>
                <div><div class="ex__caps">Proposals</div><div class="ex__big">{{ stats.proposals }}</div></div>
                <div><div class="ex__caps">Graduated · 30d</div><div class="ex__big">{{ stats.graduated }}</div></div>
            </div>
        </mkt-ws-card>

        <mkt-ws-card>
            <div style="display:flex; align-items:center; justify-content:space-between; margin-bottom:10px;">
                <div class="ex__caps">Confidence framework</div>
                <mkt-ws-pill variant="muted">Read-only</mkt-ws-pill>
            </div>
            <div class="ex__confidence-body">At Sidecar's volume statistical significance isn't reachable per experiment. The agent decides on threshold rules, not p-values.</div>
            <div style="display:flex; flex-direction:column; gap:6px; margin-top:12px;">
                <div class="ex__conf-row"><mkt-ws-pill variant="good">KEEP</mkt-ws-pill><span>{{ thresholdSummary.keep }}</span></div>
                <div class="ex__conf-row"><mkt-ws-pill variant="warn">ITERATE</mkt-ws-pill><span>{{ thresholdSummary.iterate }}</span></div>
                <div class="ex__conf-row"><mkt-ws-pill variant="bad">KILL</mkt-ws-pill><span>{{ thresholdSummary.kill }}</span></div>
            </div>
        </mkt-ws-card>
    </div>

    <!-- Active experiments table -->
    <mkt-ws-card class="ex__sec" [hasHead]="true" [noPadding]="true">
        <div head>
            <div style="display:flex; align-items:center; gap:8px;">
                <span class="ex__caps-ink">Active experiments · {{ active.length }}</span>
                <mkt-ws-pill *ngIf="stats.decisionsDue > 0" variant="warn">{{ stats.decisionsDue }} decisions due</mkt-ws-pill>
            </div>
            <span class="ex__sub">Tiered: exploit → fill → explore</span>
        </div>
        <table class="ex__table" *ngIf="active.length > 0; else noActive">
            <thead>
                <tr>
                    <th class="ex__t-id">ID</th>
                    <th>Experiment</th>
                    <th class="ex__t-tier">Tier</th>
                    <th class="ex__t-num">Spend</th>
                    <th class="ex__t-num">Decision date</th>
                    <th class="ex__t-conf">Confidence</th>
                </tr>
            </thead>
            <tbody>
                <tr *ngFor="let row of active" (click)="openDetail(row.ID)">
                    <td class="ex__t-id ex__mono">{{ row.Code || row.ID.slice(0, 8) }}</td>
                    <td>
                        <div class="ex__t-name">{{ row.Name || row.Hypothesis || '—' }}</div>
                        <div class="ex__t-sub">{{ row.Channel || '—' }} · {{ row.Tactic || '—' }}</div>
                    </td>
                    <td>
                        <mkt-ws-pill variant="muted">{{ row.ExperimentTier || '—' }}</mkt-ws-pill>
                        <mkt-ws-pill *ngIf="row.RequiresOverride" variant="warn" [title]="overrideTitle(row)" style="margin-left: 6px;">
                            Held · over cap
                        </mkt-ws-pill>
                    </td>
                    <td class="ex__t-num ex__mono">{{ formatBudget(row.Budget) }}</td>
                    <td class="ex__t-num ex__mono">{{ formatDate(row.DecisionDate) }}</td>
                    <td><mkt-ws-pill [variant]="confidenceVariant(row.ProposalConfidence)">{{ row.ProposalConfidence || '—' }}</mkt-ws-pill></td>
                </tr>
            </tbody>
        </table>
        <ng-template #noActive>
            <div class="ex__empty">No experiments are running. Approve a proposal above or create one from the Propose page.</div>
        </ng-template>
    </mkt-ws-card>

    <!-- Graduated -->
    <mkt-ws-card class="ex__sec" [hasHead]="true" [noPadding]="true">
        <div head>
            <div style="display:flex; align-items:center; gap:8px;">
                <span class="ex__caps-ink">Graduated · Evergreen programs</span>
                <mkt-ws-pill variant="good">{{ evergreen.length }} live</mkt-ws-pill>
            </div>
            <span class="ex__sub">Pushed into Programs · monthly floor checks</span>
        </div>
        <table class="ex__table" *ngIf="evergreen.length > 0; else noEvergreen">
            <thead>
                <tr>
                    <th>Program</th>
                    <th>Channel</th>
                    <th class="ex__t-num">Floor (KEEP threshold)</th>
                    <th class="ex__t-num">Decided</th>
                </tr>
            </thead>
            <tbody>
                <tr *ngFor="let row of evergreen" (click)="openDetail(row.ID)">
                    <td class="ex__t-name">{{ row.Name || row.Hypothesis || '—' }}</td>
                    <td>{{ row.Channel || '—' }}</td>
                    <td class="ex__t-num ex__mono">{{ row.KeepThreshold ?? '—' }}</td>
                    <td class="ex__t-num ex__mono">{{ formatDate(row.DecisionDate) }}</td>
                </tr>
            </tbody>
        </table>
        <ng-template #noEvergreen>
            <div class="ex__empty">No evergreen programs yet. Experiments graduate here when the agent recommends KEEP and you approve.</div>
        </ng-template>
    </mkt-ws-card>

    <details class="ex__debug" *ngIf="debugInfo.queryRan">
        <summary>Debug — query state</summary>
        <div><strong>Company ID:</strong> <code>{{ companyId || '(empty)' }}</code></div>
        <div><strong>Statuses loaded:</strong> {{ debugInfo.statusCount }}</div>
        <div><strong>Experiments loaded:</strong> {{ debugInfo.expCount }}</div>
        <div><strong>Sprints loaded:</strong> {{ debugInfo.sprintCount }}</div>
        <div *ngIf="debugInfo.errorMsg"><strong>Error:</strong> <code>{{ debugInfo.errorMsg }}</code></div>
    </details>
</ng-container>
    `,
    styles: [`
        :host { display: block; }
        .ex__loading { padding: 60px 20px; text-align: center; color: var(--mj-text-secondary, #71717a); }

        .ex__btn { display: inline-flex; align-items: center; gap: 6px; padding: 6px 12px; height: 30px; border: 1px solid var(--mj-border-default, #e0e0e0); background: var(--mj-bg-surface, #fff); border-radius: 6px; font: inherit; font-size: 12px; cursor: pointer; color: var(--mj-text-primary, #1a1a1a); transition: background 0.15s; }
        .ex__btn:hover { background: var(--mj-bg-page, #f5f5f5); }
        .ex__btn:disabled { opacity: 0.5; cursor: not-allowed; }
        .ex__btn--primary { background: var(--mj-text-primary, #1a1a1a); color: var(--mj-bg-surface, #fff); border-color: var(--mj-text-primary, #1a1a1a); }
        .ex__btn--primary:hover { background: var(--mj-text-primary, #000); }

        .ex__caps, .ex__caps-ink { font-size: 10.5px; letter-spacing: 0.11em; text-transform: uppercase; font-weight: 500; color: var(--mj-text-secondary, #71717a); }
        .ex__caps-ink { color: var(--mj-text-primary, #1a1a1a); }
        .ex__sub { font-size: 11px; color: var(--mj-text-secondary, #71717a); }

        .ex__sec { margin-bottom: 24px; }
        .ex__row { padding: 18px 20px; border-bottom: 1px solid var(--mj-border-default, #e0e0e0); }
        .ex__row:last-child { border-bottom: none; }
        .ex__row-head { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 8px; gap: 8px; }
        .ex__ts { font-size: 11px; color: var(--mj-text-secondary, #71717a); font-family: ui-monospace, monospace; flex-shrink: 0; }
        .ex__row-title { font-size: 14px; font-weight: 500; line-height: 1.4; margin-bottom: 4px; color: var(--mj-text-primary, #1a1a1a); }
        .ex__row-body { font-size: 12.5px; color: var(--mj-text-secondary, #71717a); line-height: 1.5; margin-bottom: 12px; }
        .ex__why { display: flex; align-items: flex-start; gap: 8px; padding: 8px 12px; background: var(--mj-bg-page, #f5f5f5); border: 1px solid var(--mj-border-default, #e0e0e0); border-radius: 6px; font-size: 11.5px; margin-bottom: 12px; }
        .ex__why > i { color: var(--mj-text-secondary, #71717a); margin-top: 2px; flex-shrink: 0; }
        .ex__why-text { margin-left: 6px; color: var(--mj-text-primary, #1a1a1a); }
        .ex__actions { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
        .ex__row-meta { margin-top: 8px; font-size: 11.5px; color: var(--mj-text-secondary, #71717a); }
        .ex__empty { padding: 32px 20px; text-align: center; font-size: 12.5px; color: var(--mj-text-secondary, #71717a); }

        .ex__split { display: grid; grid-template-columns: 2fr 1fr; gap: 16px; margin-bottom: 24px; }
        .ex__sprint-head { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 16px; gap: 12px; }
        .ex__sprint-title { font-size: 18px; font-weight: 600; color: var(--mj-text-primary, #1a1a1a); margin-top: 4px; line-height: 1.2; }
        .ex__sprint-bar-labels { display: flex; gap: 4px; font-size: 10px; letter-spacing: 0.11em; text-transform: uppercase; font-weight: 500; color: var(--mj-text-secondary, #71717a); margin-bottom: 6px; }
        .ex__sprint-bar { display: flex; gap: 4px; height: 10px; margin-bottom: 16px; }
        .ex__sprint-seg { background: var(--mj-border-default, #e0e0e0); border-radius: 2px; }
        .ex__sprint-seg--active { background: var(--mj-text-primary, #1a1a1a); }
        .ex__sprint-seg--current { background: var(--mj-text-primary, #1a1a1a); opacity: 0.45; }
        .ex__sprint-stats { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; font-size: 12.5px; }
        .ex__big { font-family: ui-monospace, monospace; font-size: 22px; font-variant-numeric: tabular-nums; color: var(--mj-text-primary, #1a1a1a); margin-top: 4px; }
        .ex__confidence-body { font-size: 12.5px; line-height: 1.55; color: var(--mj-text-primary, #1a1a1a); }
        .ex__conf-row { display: flex; align-items: center; gap: 8px; font-size: 12px; color: var(--mj-text-secondary, #71717a); }

        .ex__table { width: 100%; border-collapse: collapse; font-size: 13px; }
        .ex__table thead th { font-size: 10.5px; letter-spacing: 0.11em; text-transform: uppercase; font-weight: 500; color: var(--mj-text-secondary, #71717a); padding: 10px 16px; text-align: left; background: var(--mj-bg-page, #f5f5f5); }
        .ex__table th.ex__t-id { width: 90px; }
        .ex__table th.ex__t-tier { width: 100px; }
        .ex__table th.ex__t-num { width: 110px; text-align: right; }
        .ex__table th.ex__t-conf { width: 110px; }
        .ex__table tbody tr { border-top: 1px solid var(--mj-border-default, #e0e0e0); cursor: pointer; transition: background 0.15s; }
        .ex__table tbody tr:hover { background: var(--mj-bg-page, #f5f5f5); }
        .ex__table td { padding: 10px 16px; vertical-align: top; }
        .ex__table td.ex__t-id { color: var(--mj-text-secondary, #71717a); }
        .ex__table td.ex__t-num { text-align: right; }
        .ex__t-name { font-weight: 500; color: var(--mj-text-primary, #1a1a1a); }
        .ex__t-sub { font-size: 11.5px; color: var(--mj-text-secondary, #71717a); margin-top: 2px; }
        .ex__mono { font-family: ui-monospace, monospace; font-variant-numeric: tabular-nums; }

        .ex__debug { margin-top: 24px; padding: 12px 16px; background: var(--mj-bg-page, #f5f5f5); border: 1px solid var(--mj-border-default, #e0e0e0); border-radius: 8px; font-size: 12px; }
        .ex__debug summary { cursor: pointer; font-weight: 600; color: var(--mj-text-secondary, #71717a); }
        .ex__debug code { background: var(--mj-bg-surface, #fff); padding: 2px 6px; border-radius: 3px; font-family: ui-monospace, monospace; font-size: 11px; }

        @media (max-width: 1100px) {
            .ex__split { grid-template-columns: 1fr; }
            .ex__sprint-stats { grid-template-columns: repeat(2, 1fr); }
        }
        .ex__btn--danger { color: var(--mj-status-error, #ef4444); }
        .ex__agent-status { display: flex; align-items: center; gap: 8px; padding: 10px 14px; margin: 0 0 16px 0; background: #e0f2fe; color: #0c4a6e; border: 1px solid #7dd3fc; border-radius: 6px; font-size: 13px; }
        .ex__agent-status--err { background: #fef2f2; color: #991b1b; border-color: #fca5a5; }
        .ex__agent-status-x { background: transparent; border: none; cursor: pointer; color: inherit; padding: 0 4px; opacity: 0.6; margin-left: auto; }
        .ex__agent-status-x:hover { opacity: 1; }
    `]
})
export class ExperimentsOverviewSection {
    private _companyId = '';
    @Input() set companyId(v: string) {
        if (v && v !== this._companyId) {
            this._companyId = v;
            void this.load();
        } else if (!v) {
            this._companyId = '';
            this.reset();
        }
    }
    get companyId(): string { return this._companyId; }

    @Input() companyName = '';
    @Input() navigate: (path: string) => void = () => {};
    @Input() navigateWithId: (path: string, id?: string) => void = () => {};

    private cdr = inject(ChangeDetectorRef);

    loading = false;
    proposed: ExperimentRow[] = [];
    active: ExperimentRow[] = [];
    evergreen: ExperimentRow[] = [];
    decided: ExperimentRow[] = [];
    statuses: marketingExperimentStatusEntity[] = [];
    tiers: marketingExperimentTierEntity[] = [];
    currentSprint: marketingSprintEntity | null = null;
    busyId = '';
    busyMessage: Record<string, string> = {};

    // Sandbox/agent controls
    agentBusy = false;
    advanceBusy = false;
    resetBusy = false;
    agentStatusMessage = '';
    agentStatusError = false;

    /** True when the loaded company is a sandbox tenant. Drives visibility of
     *  Advance / Reset buttons. Name-suffix detection matches the server-side
     *  sandboxDetection.ts (no need to look up BrandConfiguration.IsSandbox from the UI). */
    get isSandbox(): boolean {
        return /\bsandbox\s*$/i.test(this.companyName || '');
    }

    debugInfo = { queryRan: false, statusCount: 0, expCount: 0, sprintCount: 0, errorMsg: '' };

    get headerEyebrow(): string {
        const sprint = this.currentSprint?.SprintNumber ? `Sprint ${this.currentSprint.SprintNumber}` : 'Experiments';
        return `${sprint}${this.companyName ? ' · ' + this.companyName : ''}`;
    }

    get currentSprintLabel(): string {
        return this.currentSprint?.SprintNumber ? `Sprint ${this.currentSprint.SprintNumber}` : 'Sprint';
    }

    get currentSprintRange(): string {
        if (!this.currentSprint) return '';
        const s = new Date(this.currentSprint.StartDate);
        const e = new Date(this.currentSprint.EndDate);
        const fmt = (d: Date) => d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
        return `${fmt(s)} → ${fmt(e)}`;
    }

    get sprintDaysRemaining(): number | null {
        if (!this.currentSprint) return null;
        const end = new Date(this.currentSprint.EndDate).getTime();
        const now = Date.now();
        const days = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
        return days > 0 ? days : 0;
    }

    get sprintPhases(): { flex: number; active: boolean; current: boolean }[] {
        if (!this.currentSprint) return [
            { flex: 1, active: false, current: false }, { flex: 3, active: false, current: false },
            { flex: 1, active: false, current: false }, { flex: 1, active: false, current: false },
        ];
        const start = new Date(this.currentSprint.StartDate).getTime();
        const end = new Date(this.currentSprint.EndDate).getTime();
        const total = end - start;
        const elapsed = Math.max(0, Math.min(total, Date.now() - start));
        const pct = total > 0 ? elapsed / total : 0;
        const phases = [
            { flex: 1, key: 'plan', endPct: 1 / 6 },
            { flex: 3, key: 'run', endPct: 4 / 6 },
            { flex: 1, key: 'review', endPct: 5 / 6 },
            { flex: 1, key: 'decide', endPct: 1 },
        ];
        return phases.map(p => {
            const startPct = p.endPct - (p.flex / 6);
            const isActive = pct >= p.endPct;
            const isCurrent = !isActive && pct >= startPct;
            return { flex: p.flex, active: isActive, current: isCurrent };
        });
    }

    get stats(): { active: number; decisionsDue: number; proposals: number; graduated: number } {
        const now = Date.now();
        const week = 7 * 24 * 60 * 60 * 1000;
        const decisionsDue = this.active.filter(r => {
            if (!r.DecisionDate) return false;
            const d = new Date(r.DecisionDate).getTime();
            return d - now < week && d - now > -week;
        }).length;
        const graduated30d = this.evergreen.filter(r => {
            if (!r.DecisionDate) return false;
            return now - new Date(r.DecisionDate).getTime() < 30 * 24 * 60 * 60 * 1000;
        }).length;
        return {
            active: this.active.length,
            decisionsDue,
            proposals: this.proposed.length,
            graduated: graduated30d,
        };
    }

    get thresholdSummary(): { keep: string; iterate: string; kill: string } {
        const a = this.active.find(r => r.KeepThreshold != null);
        if (!a) return {
            keep: '≥ KEEP threshold',
            iterate: 'Within iterate range',
            kill: '≤ KILL threshold',
        };
        return {
            keep: `≥ ${a.KeepThreshold} (${a.Channel || 'channel'})`,
            iterate: `${a.IterateRangeMin}–${a.IterateRangeMax}`,
            kill: `≤ ${a.KillThreshold}`,
        };
    }

    overrideTitle(row: ExperimentRow): string {
        const budget = row.Budget ?? 0;
        return `Budget $${Math.round(budget).toLocaleString()} exceeds the configured tier cap. Founder sign-off required before lock.`;
    }

    confidenceVariant(c: string | null | undefined): 'good' | 'warn' | 'bad' | 'muted' {
        switch ((c || '').toLowerCase()) {
            case 'strong': return 'good';
            case 'moderate': return 'warn';
            case 'weak': return 'bad';
            default: return 'muted';
        }
    }

    formatBudget(b: number | null | undefined): string {
        if (b == null) return '—';
        if (b === 0) return '$0';
        return `$${Math.round(b).toLocaleString()}`;
    }

    formatDate(d: Date | null | undefined): string {
        if (!d) return '—';
        return new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    }

    goto(path: string): void {
        this.navigate(path);
    }

    openDetail(id: string): void {
        this.navigateWithId('experiments/detail', id);
    }

    // ─── Sandbox / agent control buttons ─────────────────────────────────

    async onRunExperimentationAgent(): Promise<void> {
        if (!this._companyId || this.agentBusy) return;
        this.agentBusy = true;
        this.agentStatusMessage = 'Invoking Marketing Experimentation Agent…';
        this.agentStatusError = false;
        this.cdr.detectChanges();
        try {
            // Resolve agent ID once.
            const rv = new RunView();
            const agentRes = await rv.RunView<{ ID: string }>({
                EntityName: 'MJ: AI Agents',
                ExtraFilter: `Name='Marketing Experimentation Agent'`,
                ResultType: 'simple',
                Fields: ['ID'],
                MaxRows: 1,
            });
            const agentId = agentRes.Success && agentRes.Results?.length ? agentRes.Results[0].ID : null;
            if (!agentId) {
                this.agentStatusError = true;
                this.agentStatusMessage = `Could not find agent "Marketing Experimentation Agent" — has metadata been synced?`;
                return;
            }
            const gql = Metadata.Provider as GraphQLDataProvider;
            const payload = {
                companyId: this._companyId,
                currentDate: new Date().toISOString().split('T')[0],
            };
            const sessionId = `experimentation-${this._companyId}-${Date.now()}`;
            const result = await gql.ExecuteGQL(
                `mutation RunAIAgent($agentId: String!, $sessionId: String!, $messages: String!, $payload: String, $fireAndForget: Boolean) {
                    RunAIAgent(agentId: $agentId, sessionId: $sessionId, messages: $messages, payload: $payload, fireAndForget: $fireAndForget) { success errorMessage result }
                }`,
                {
                    agentId,
                    sessionId,
                    messages: JSON.stringify([{ role: 'user', content: 'Propose your next experiment.' }]),
                    payload: JSON.stringify(payload),
                    fireAndForget: false,
                }
            );
            const response = result?.RunAIAgent;
            if (!response?.success) {
                this.agentStatusError = true;
                this.agentStatusMessage = response?.errorMessage || 'Agent run failed.';
                return;
            }
            this.agentStatusMessage = 'Agent finished. Reloading experiments…';
            await this.load();
            this.agentStatusMessage = 'Agent finished. Check Proposed for the new experiment(s).';
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : String(err);
            LogError(`[ExperimentsOverview] onRunExperimentationAgent failed: ${msg}`);
            this.agentStatusError = true;
            this.agentStatusMessage = msg;
        } finally {
            this.agentBusy = false;
            this.cdr.detectChanges();
        }
    }

    async onSimulateWeek(): Promise<void> {
        if (!this._companyId || this.advanceBusy) return;
        this.advanceBusy = true;
        this.agentStatusMessage = 'Advancing 1 week + auto-deciding overdue experiments…';
        this.agentStatusError = false;
        this.cdr.detectChanges();
        try {
            const result = await runAction('Simulate Sandbox Week', [
                { Name: 'CompanyID', Value: this._companyId },
                { Name: 'Weeks', Value: '1' },
                { Name: 'AutoDecide', Value: 'true' },
            ]);
            if (result.success) {
                this.agentStatusMessage = result.message || 'Simulated 1 week.';
                await this.load();
            } else {
                this.agentStatusError = true;
                this.agentStatusMessage = `Simulate failed (${result.resultCode}): ${result.message}`;
            }
        } finally {
            this.advanceBusy = false;
            this.cdr.detectChanges();
        }
    }

    async onResetSandbox(mode: 'Full' | 'KeepStrategy' = 'Full'): Promise<void> {
        if (!this._companyId || this.resetBusy) return;
        const promptBody = mode === 'KeepStrategy'
            ? `Reset content for "${this.companyName}" (keep strategy)?\n\n` +
              `HARD-DELETES blogs, social posts, newsletters, content plans, content sources, experiments, and related junctions.\n\n` +
              `PRESERVES the Strategy Document, all 7 Foundations, Agent Learnings, Company row, Authorized Users, and Brand/Channel/Template configurations.\n\n` +
              `Use this to re-test the experimentation loop without redoing onboarding.\n\nProceed?`
            : `Reset sandbox tenant "${this.companyName}" (full wipe)?\n\n` +
              `This will SUPERSEDE all StrategyDocuments (including the 7 Foundations) and HARD-DELETE blogs, social posts, newsletters, content plans, content sources, experiments, agent learnings, and related junctions.\n\n` +
              `Preserves: Company row, Authorized Users, Brand/Channel/Template configurations.\n\n` +
              `Onboarding will need to be re-run. Proceed?`;
        const ok = window.confirm(promptBody);
        if (!ok) return;
        this.resetBusy = true;
        this.agentStatusMessage = mode === 'KeepStrategy' ? 'Wiping content (strategy preserved)…' : 'Wiping sandbox tenant…';
        this.agentStatusError = false;
        this.cdr.detectChanges();
        try {
            const result = await runAction('Reset Sandbox Tenant', [
                { Name: 'CompanyID', Value: this._companyId },
                { Name: 'Confirm', Value: 'YES_WIPE_GENERATED_CONTENT' },
                { Name: 'Mode', Value: mode },
            ]);
            if (result.success) {
                this.agentStatusMessage = result.message || 'Reset complete.';
                await this.load();
            } else {
                this.agentStatusError = true;
                this.agentStatusMessage = `Reset failed (${result.resultCode}): ${result.message}`;
            }
        } finally {
            this.resetBusy = false;
            this.cdr.detectChanges();
        }
    }

    async onApprove(row: ExperimentRow): Promise<void> {
        this.busyId = row.ID;
        this.busyMessage = { ...this.busyMessage, [row.ID]: 'Locking experiment…' };
        this.cdr.detectChanges();
        const result = await runAction('Lock Experiment', [
            { Name: 'CompanyID', Value: this._companyId },
            { Name: 'ExperimentID', Value: row.ID },
        ]);
        if (result.success) {
            this.busyMessage = { ...this.busyMessage, [row.ID]: 'Locked. Refreshing…' };
            await this.load();
        } else {
            this.busyMessage = { ...this.busyMessage, [row.ID]: `Lock failed: ${result.message}` };
        }
        this.busyId = '';
        this.cdr.detectChanges();
    }

    private async load(): Promise<void> {
        if (!this._companyId) return;
        const cid = this._companyId;
        this.loading = true;
        this.debugInfo = { queryRan: true, statusCount: 0, expCount: 0, sprintCount: 0, errorMsg: '' };
        this.cdr.detectChanges();
        try {
            const rv = new RunView();
            const [statusRes, tierRes, expRes, sprintRes] = await rv.RunViews([
                { EntityName: 'Experiment Status', ResultType: 'simple' },
                { EntityName: 'Experiment Tiers', ResultType: 'simple' },
                {
                    EntityName: 'Experiments',
                    ExtraFilter: `CompanyID='${cid}'`,
                    OrderBy: 'DecisionDate ASC, __mj_CreatedAt DESC',
                    MaxRows: 200,
                    ResultType: 'simple',
                },
                {
                    EntityName: 'Sprints',
                    ExtraFilter: `CompanyID='${cid}'`,
                    OrderBy: 'StartDate DESC',
                    MaxRows: 6,
                    ResultType: 'entity_object',
                },
            ]);

            this.debugInfo.statusCount = statusRes.Results?.length ?? 0;
            this.debugInfo.expCount = expRes.Results?.length ?? 0;
            this.debugInfo.sprintCount = sprintRes.Results?.length ?? 0;

            if (statusRes.Success) this.statuses = statusRes.Results as marketingExperimentStatusEntity[];
            if (tierRes.Success) this.tiers = tierRes.Results as marketingExperimentTierEntity[];

            if (expRes.Success) {
                const all = (expRes.Results || []) as ExperimentRow[];
                this.proposed = all.filter(r => r.ExperimentStatus === 'Proposed');
                this.active = all.filter(r => r.ExperimentStatus === 'Active');
                this.evergreen = all.filter(r => r.ExperimentStatus === 'Evergreen');
                this.decided = all.filter(r => r.ExperimentStatus === 'Decided');
            } else if (expRes.ErrorMessage) {
                this.debugInfo.errorMsg = expRes.ErrorMessage;
                LogError(`[ExperimentsOverview] experiments query failed: ${expRes.ErrorMessage}`);
            }

            if (sprintRes.Success) {
                const sprints = (sprintRes.Results || []) as marketingSprintEntity[];
                const active = sprints.find(s => s.Status === 'Active' || s.Status === 'Review');
                this.currentSprint = active || sprints[0] || null;
            }
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : String(err);
            LogError(`[ExperimentsOverview] load failed: ${msg}`);
            this.debugInfo.errorMsg = msg;
        } finally {
            this.loading = false;
            this.cdr.detectChanges();
        }
    }

    private reset(): void {
        this.proposed = [];
        this.active = [];
        this.evergreen = [];
        this.decided = [];
        this.currentSprint = null;
        this.cdr.detectChanges();
    }
}
